// src/components/TreatmentsTab.js
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Activity, X, Trash2, Receipt, Link as LinkIcon, Copy, CheckCircle, Edit3, Printer, FileText, TableProperties } from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function resolvePatientId(props) {
  const fromSelected = props?.selectedPatient?.id;
  const fromProp =
    props?.patientId ||
    props?.patient_id ||
    props?.patient?.id ||
    props?.patient?.patient_id ||
    props?.patient?.patientId;
  const fromStorage = (() => {
    try { return window.localStorage.getItem('currentPatientId') || null; } catch { return null; }
  })();
  const pid = fromSelected || fromProp || fromStorage || null;
  if (pid && pid !== fromStorage) {
    try { window.localStorage.setItem('currentPatientId', pid); } catch {}
  }
  return pid;
}

const TreatmentsTab = ({
  patientData,
  selectedPatient,
  toast,
  formatDate,
  formatCurrency,
  onDataUpdate,
  logAudit,
  initialFilter = '',
}) => {
  const patientId = resolvePatientId({ selectedPatient });

  const [showNewTreatmentModal, setShowNewTreatmentModal] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [showPrintListModal, setShowPrintListModal] = useState(false);
  const [printPeriodStart, setPrintPeriodStart] = useState('');
  const [printPeriodEnd, setPrintPeriodEnd] = useState('');
  const [printActiveFilter, setPrintActiveFilter] = useState('todos');
  const [printAcceptanceFilter, setPrintAcceptanceFilter] = useState('todos');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [listFilter, setListFilter] = useState(
    initialFilter === 'stalled' ? 'stalled' : ''
  );

  const [treatments, setTreatments] = useState([]);

  const [alignerTreatments, setAlignerTreatments] =
    useState([]);

  const [priceTable, setPriceTable] = useState([]);
  const [showPriceTableModal, setShowPriceTableModal] = useState(false);
  const [editingPriceItem, setEditingPriceItem] = useState(null);
  const [savingPriceItem, setSavingPriceItem] = useState(false);
  const [priceItemForm, setPriceItemForm] = useState({
    procedure: '',
    description: '',
    default_cost: '',
    default_priority: 'Média',
    is_active: true,
  });

  // New states for Faturamento
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedTreatmentForInvoice, setSelectedTreatmentForInvoice] = useState(null);
  const [invoiceOption, setInvoiceOption] = useState('new'); // 'new' or 'link'
  const [selectedExistingInvoiceId, setSelectedExistingInvoiceId] = useState('');
  const [invoicesList, setInvoicesList] = useState([]);
  const [isInvoicing, setIsInvoicing] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const loadPriceTable = async () => {
    try {
      const res = await axios.get(`${API_URL}/treatments/price-table`, {
        headers: getAuthHeaders(),
      });

      setPriceTable(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error('Erro ao carregar tabela de valores:', error);
    }
  };

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!patientId) {
        setLoading(false);
        setErr('Paciente não informado');
        return;
      }
      setLoading(true);
      setErr(null);
      try {
        const [
          treatmentsResponse,
          alignersResponse,
        ] = await Promise.all([
          axios.get(`${API_URL}/treatments`, {
            params: {
              patient_id: patientId,
            },
            headers: getAuthHeaders(),
          }),

          axios.get(
            `${API_URL}/aligner-treatments/patient/${patientId}`,
            {
              headers: getAuthHeaders(),
            }
          ),
        ]);
        const arr = Array.isArray(
          treatmentsResponse?.data?.items
        )
          ? treatmentsResponse.data.items
          : Array.isArray(treatmentsResponse?.data)
          ? treatmentsResponse.data
          : [];

        const alignerList = Array.isArray(
          alignersResponse?.data
        )
          ? alignersResponse.data
          : [];
        if (mounted) setTreatments(arr);
        if (mounted) {
          setAlignerTreatments(alignerList);
        }
        if (mounted) await loadPriceTable();
      } catch (e) {
        if (mounted) setErr('Falha ao carregar tratamentos');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [patientId]);

  const [treatmentFormData, setTreatmentFormData] = useState({
    procedure: '',
    description: '',
    cost: '',
    discount_percent: '',
    discount_amount: '',
    final_cost: '',
    priority: 'Média',
    status: 'Planejado',

    // NOVOS CAMPOS CFO
    diagnosis: '',
    treatment_plan: '',
    alternatives: '',
    risks: '',
  });

  const orderedTreatments = useMemo(() => {
    const list = [...(treatments || [])];
    return list.sort((a, b) => {
      const da = new Date(a?.created_at || a?.date_performed || 0).getTime();
      const db = new Date(b?.created_at || b?.date_performed || 0).getTime();
      return db - da;
    });
  }, [treatments]);

  const visibleTreatments = useMemo(() => {
    if (listFilter !== 'stalled') return orderedTreatments;

    const now = new Date();

    return orderedTreatments.filter((treatment) => {
      const status = String(treatment?.status || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

      const activeStatus =
        status.includes('andamento') ||
        status.includes('ativo') ||
        status.includes('in_progress');

      if (!activeStatus) return false;

      const referenceValue =
        treatment?.updated_at ||
        treatment?.start_date ||
        treatment?.created_at;

      if (!referenceValue) return false;

      const reference = new Date(referenceValue);
      if (Number.isNaN(reference.getTime())) return false;

      const days = Math.floor((now - reference) / 86400000);
      return days >= 90;
    });
  }, [orderedTreatments, listFilter]);

  const alignerByLinkedTreatmentId = useMemo(() => {
    const map = new Map();

    (alignerTreatments || []).forEach(
      (alignerTreatment) => {
        const linkedId =
          alignerTreatment?.linked_treatment_id;

        if (!linkedId) {
          return;
        }

        map.set(linkedId, alignerTreatment);
      }
    );

    return map;
  }, [alignerTreatments]);

  const safeNumber = (val) => {
    if (val === null || val === undefined) return undefined;
    if (typeof val === 'number') return Number.isFinite(val) ? val : undefined;
    const trimmed = String(val).trim().replace(',', '.');
    if (!trimmed) return undefined;
    const n = parseFloat(trimmed);
    return Number.isFinite(n) ? n : undefined;
  };

  const calculateFinalCost = (costValue, discountPercentValue) => {
    const cost = safeNumber(costValue);
    const discountPercent = safeNumber(discountPercentValue);

    if (cost === undefined) {
      return {
        cost: undefined,
        discountPercent: undefined,
        discountAmount: undefined,
        finalCost: undefined,
      };
    }

    const safeDiscountPercent =
      discountPercent !== undefined
        ? Math.min(Math.max(discountPercent, 0), 100)
        : 0;

    const discountAmount = Number((cost * safeDiscountPercent / 100).toFixed(2));
    const finalCost = Number((cost - discountAmount).toFixed(2));

    return {
      cost,
      discountPercent: safeDiscountPercent,
      discountAmount,
      finalCost,
    };
  };

  const resetPriceItemForm = () => {
    setEditingPriceItem(null);
    setPriceItemForm({
      procedure: '',
      description: '',
      default_cost: '',
      default_priority: 'Média',
      is_active: true,
    });
  };

  const handleOpenPriceTableModal = () => {
    resetPriceItemForm();
    setShowPriceTableModal(true);
    loadPriceTable();
  };

  const handleEditPriceItem = (item) => {
    setEditingPriceItem(item);
    setPriceItemForm({
      procedure: item.procedure || '',
      description: item.description || '',
      default_cost: item.default_cost != null ? String(item.default_cost).replace('.', ',') : '',
      default_priority: item.default_priority || 'Média',
      is_active: item.is_active !== false,
    });
  };

  const handleSavePriceItem = async (e) => {
    e.preventDefault();

    if (!priceItemForm.procedure.trim()) {
      toast?.error?.('Informe o nome do tratamento.');
      return;
    }

    const costNum = safeNumber(priceItemForm.default_cost);

    const payload = {
      procedure: priceItemForm.procedure.trim(),
      description: priceItemForm.description?.trim() || undefined,
      default_priority: priceItemForm.default_priority || 'Média',
      is_active: Boolean(priceItemForm.is_active),
    };

    if (costNum !== undefined) {
      payload.default_cost = costNum;
    }

    try {
      setSavingPriceItem(true);

      if (editingPriceItem?.id) {
        const res = await axios.put(
          `${API_URL}/treatments/price-table/${editingPriceItem.id}`,
          payload,
          { headers: getAuthHeaders() }
        );

        setPriceTable((prev) =>
          prev.map((item) => (item.id === editingPriceItem.id ? res.data : item))
        );

        toast?.success?.('Item atualizado na tabela de valores.');
      } else {
        const res = await axios.post(`${API_URL}/treatments/price-table`, payload, {
          headers: getAuthHeaders(),
        });

        setPriceTable((prev) => [...prev, res.data].sort((a, b) =>
          String(a.procedure || '').localeCompare(String(b.procedure || ''))
        ));

        toast?.success?.('Item adicionado à tabela de valores.');
      }

      resetPriceItemForm();
    } catch (error) {
      toast?.error?.(extractErrorMsg(error, 'Erro ao salvar item da tabela.'));
    } finally {
      setSavingPriceItem(false);
    }
  };

  const handleDeletePriceItem = async (item) => {
    if (!item?.id) return;

    const confirmed = window.confirm(
      `Remover "${item.procedure}" da tabela de valores?`
    );

    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/treatments/price-table/${item.id}`, {
        headers: getAuthHeaders(),
      });

      setPriceTable((prev) => prev.filter((p) => p.id !== item.id));
      toast?.success?.('Item removido da tabela de valores.');
    } catch (error) {
      toast?.error?.(extractErrorMsg(error, 'Erro ao remover item da tabela.'));
    }
  };

  const handleSelectPriceTableItem = (itemId) => {
    const item = priceTable.find((p) => p.id === itemId);

    if (!item) return;

    setTreatmentFormData((prev) => ({
      ...prev,
      procedure: item.procedure || prev.procedure,
      description: item.description || prev.description,
      cost: item.default_cost != null ? String(item.default_cost).replace('.', ',') : prev.cost,
      priority: item.default_priority || prev.priority,
    }));
  };

  const extractErrorMsg = (error, fallback = 'Ocorreu um erro.') => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail) && detail.length) {
      return detail.map((d) => d?.msg || JSON.stringify(d)).join(' | ');
    }
    if (typeof detail === 'string') return detail;
    if (error?.message) return error.message;
    return fallback;
  };

  const resetTreatmentForm = () => {
    setSelectedTreatment(null);
    setTreatmentFormData({
      procedure: '',
      description: '',
      cost: '',
      discount_percent: '',
      discount_amount: '',
      final_cost: '',
      priority: 'Média',
      status: 'Planejado',
      diagnosis: '',
      treatment_plan: '',
      alternatives: '',
      risks: '',
    });
  };

  const closeTreatmentModal = () => {
    setShowNewTreatmentModal(false);
    resetTreatmentForm();
  };

  const handleOpenTreatment = (treatment) => {
    setSelectedTreatment(treatment);
    setTreatmentFormData({
      procedure: treatment.procedure || '',
      description: treatment.description || '',
      cost: treatment.cost != null ? String(treatment.cost).replace('.', ',') : '',
      discount_percent: treatment.discount_percent != null ? String(treatment.discount_percent).replace('.', ',') : '',
      discount_amount: treatment.discount_amount != null ? String(treatment.discount_amount).replace('.', ',') : '',
      final_cost: treatment.final_cost != null ? String(treatment.final_cost).replace('.', ',') : '',
      priority: treatment.priority || 'Média',
      status: treatment.status || 'Planejado',
      diagnosis: treatment.diagnosis || '',
      treatment_plan: treatment.treatment_plan || '',
      alternatives: treatment.alternatives || '',
      risks: treatment.risks || '',
    });
    setShowNewTreatmentModal(true);
  };

  const handlePrintTreatment = (treatment) => {
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      toast?.error?.('Não foi possível abrir a janela de impressão.');
      return;
    }

    const costText =
      treatment.cost != null && treatment.cost !== ''
        ? (formatCurrency
            ? formatCurrency(treatment.cost)
            : (Number(treatment.cost) || 0).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }))
        : '—';

    const finalCostText =
      treatment.final_cost != null && treatment.final_cost !== ''
        ? (formatCurrency
            ? formatCurrency(treatment.final_cost)
            : (Number(treatment.final_cost) || 0).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }))
        : costText;

    const discountText =
      treatment.discount_percent
        ? `${treatment.discount_percent}%`
        : '—';

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Plano de Tratamento</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; margin: 32px; line-height: 1.45; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            h2 { font-size: 16px; margin-top: 24px; border-bottom: 1px solid #d1d5db; padding-bottom: 6px; }
            .muted { color: #6b7280; font-size: 13px; }
            .row { margin: 8px 0; }
            strong { color: #111827; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          <h1>Plano de Tratamento</h1>
          <div class="muted">Gerado pelo OdontoAssist AI</div>

          <h2>Paciente</h2>
          <div class="row"><strong>Nome:</strong> ${selectedPatient?.full_name || treatment.patient_name || '—'}</div>

          <h2>Tratamento</h2>
          <div class="row"><strong>Procedimento:</strong> ${treatment.procedure || '—'}</div>
          <div class="row"><strong>Status:</strong> ${treatment.status || '—'}</div>
          <div class="row"><strong>Prioridade:</strong> ${treatment.priority || '—'}</div>
          <div class="row"><strong>Custo:</strong> ${costText}</div>

          <div class="row"><strong>Desconto:</strong> ${discountText}</div>
          <div class="row"><strong>Valor final:</strong> ${finalCostText}</div>

          <h2>Informações clínicas</h2>
          <div class="row"><strong>Descrição:</strong><br />${treatment.description || '—'}</div>
          <div class="row"><strong>Diagnóstico / achado clínico:</strong><br />${treatment.diagnosis || '—'}</div>
          <div class="row"><strong>Plano proposto:</strong><br />${treatment.treatment_plan || '—'}</div>
          <div class="row"><strong>Alternativas terapêuticas:</strong><br />${treatment.alternatives || '—'}</div>
          <div class="row"><strong>Riscos e limitações:</strong><br />${treatment.risks || '—'}</div>

          <h2>Aceite do paciente</h2>
          <div class="row"><strong>Status:</strong> ${treatment.patient_acceptance_status || 'pendente'}</div>
          ${
            treatment.patient_acceptance_at
              ? `<div class="row"><strong>Confirmado em:</strong> ${new Date(treatment.patient_acceptance_at).toLocaleString('pt-BR')}</div>`
              : ''
          }

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handlePrintTreatmentsList = () => {
    const startDate = printPeriodStart ? new Date(`${printPeriodStart}T00:00:00`) : null;
    const endDate = printPeriodEnd ? new Date(`${printPeriodEnd}T23:59:59`) : null;

    const filtered = orderedTreatments.filter((t) => {
      if (printActiveFilter === 'ativos' && t.is_inactive) return false;
      if (printActiveFilter === 'inativos' && !t.is_inactive) return false;

      if (printAcceptanceFilter === 'aceitos' && t.patient_acceptance_status !== 'aceito') return false;
      if (printAcceptanceFilter === 'pendentes' && t.patient_acceptance_status === 'aceito') return false;

      const baseDate = t.created_at || t.date_performed || t.updated_at;
      if (!baseDate) return !startDate && !endDate;

      const d = new Date(baseDate);
      if (Number.isNaN(d.getTime())) return !startDate && !endDate;

      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;

      return true;
    });

    if (filtered.length === 0) {
      toast?.error?.('Nenhum tratamento encontrado para o período selecionado.');
      return;
    }

    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      toast?.error?.('Não foi possível abrir a janela de impressão.');
      return;
    }

    const periodLabel =
      printPeriodStart || printPeriodEnd
        ? `${printPeriodStart ? new Date(`${printPeriodStart}T00:00:00`).toLocaleDateString('pt-BR') : 'início'} até ${printPeriodEnd ? new Date(`${printPeriodEnd}T00:00:00`).toLocaleDateString('pt-BR') : 'hoje'}`
        : 'Todos os tratamentos';

    const activeLabel =
      printActiveFilter === 'ativos'
        ? 'Somente planos ativos'
        : printActiveFilter === 'inativos'
        ? 'Somente planos inativos'
        : 'Planos ativos e inativos';

    const acceptanceLabel =
      printAcceptanceFilter === 'aceitos'
        ? 'Somente planos com aceite'
        : printAcceptanceFilter === 'pendentes'
        ? 'Somente planos sem aceite'
        : 'Com e sem aceite';

    const totalGrossCost = filtered.reduce((sum, item) => {
      const value = Number(item.cost);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);

    const totalDiscountAmount = filtered.reduce((sum, item) => {
      const value = Number(item.discount_amount);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);

    const totalFinalCost = filtered.reduce((sum, item) => {
      const baseValue = item.final_cost ?? item.cost;
      const value = Number(baseValue);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);

    const totalGrossCostText = formatCurrency
      ? formatCurrency(totalGrossCost)
      : totalGrossCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const totalDiscountAmountText = formatCurrency
      ? formatCurrency(totalDiscountAmount)
      : totalDiscountAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const totalFinalCostText = formatCurrency
      ? formatCurrency(totalFinalCost)
      : totalFinalCost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const itemsHtml = filtered.map((t, index) => {
      const costText =
        t.cost != null && t.cost !== ''
          ? (formatCurrency
              ? formatCurrency(t.cost)
              : (Number(t.cost) || 0).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }))
          : '—';

      const discountText =
        t.discount_percent
          ? `${t.discount_percent}%`
          : '—';

      const discountAmountText =
        t.discount_amount != null && t.discount_amount !== ''
          ? (formatCurrency
              ? formatCurrency(t.discount_amount)
              : (Number(t.discount_amount) || 0).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }))
          : '—';

      const finalCostText =
        t.final_cost != null && t.final_cost !== ''
          ? (formatCurrency
              ? formatCurrency(t.final_cost)
              : (Number(t.final_cost) || 0).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }))
          : costText;

      const baseDate = t.created_at || t.date_performed || t.updated_at;
      const dateText = baseDate
        ? new Date(baseDate).toLocaleDateString('pt-BR')
        : '—';

      return `
        <tr>
          <td>${index + 1}</td>
          <td>
            <strong>${t.procedure || '—'}</strong>
            ${t.description ? `<div class="muted">${t.description}</div>` : ''}
            ${t.diagnosis ? `<div><strong>Diagnóstico:</strong> ${t.diagnosis}</div>` : ''}
            ${t.treatment_plan ? `<div><strong>Plano:</strong> ${t.treatment_plan}</div>` : ''}
          </td>
          <td>${t.status || '—'}</td>
          <td>${t.priority || '—'}</td>
          <td>${dateText}</td>
          <td>${costText}</td>
          <td>${discountText}</td>
          <td>${discountAmountText}</td>
          <td><strong>${finalCostText}</strong></td>
        </tr>
      `;
    }).join('');

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Resumo de Tratamentos</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; margin: 28px; line-height: 1.35; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            h2 { font-size: 15px; margin-top: 22px; }
            .muted { color: #6b7280; font-size: 12px; margin-top: 3px; }
            .summary { display: flex; gap: 18px; margin: 18px 0; padding: 12px; border: 1px solid #d1d5db; border-radius: 8px; }
            .summary div { font-size: 13px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 12px; }
            th, td { border: 1px solid #d1d5db; padding: 7px; vertical-align: top; }
            th { background: #f3f4f6; text-align: left; }
            @media print { body { margin: 18mm; } }
          </style>
        </head>
        <body>
          <h1>Resumo dos Tratamentos</h1>
          <div class="muted">Gerado pelo OdontoAssist AI em ${new Date().toLocaleString('pt-BR')}</div>

          <h2>Paciente</h2>
          <div><strong>Nome:</strong> ${selectedPatient?.full_name || patientData?.full_name || '—'}</div>

          <div class="summary">
            <div><strong>Período:</strong><br />${periodLabel}</div>
            <div><strong>Status:</strong><br />${activeLabel}</div>
            <div><strong>Aceite:</strong><br />${acceptanceLabel}</div>
            <div><strong>Total de tratamentos:</strong><br />${filtered.length}</div>
            <div><strong>Soma bruta:</strong><br />${totalGrossCostText}</div>
            <div><strong>Total descontos:</strong><br />${totalDiscountAmountText}</div>
            <div><strong>Total final:</strong><br />${totalFinalCostText}</div>
          </div>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Tratamento</th>
                <th>Status</th>
                <th>Prioridade</th>
                <th>Data</th>
                <th>Custo bruto</th>
                <th>Desc. %</th>
                <th>Desc. R$</th>
                <th>Valor final</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    setShowPrintListModal(false);
  };

  const handleNewTreatmentSubmit = async (e) => {
    e.preventDefault();

    if (!patientId || !selectedPatient?.full_name) {
      toast?.error?.('Paciente inválido. Reabra o prontuário e tente novamente.');
      return;
    }
    if (!treatmentFormData.procedure.trim()) {
      toast?.error?.('Informe o procedimento.');
      return;
    }

    const payload = {
      patient_id: patientId,
      patient_name: selectedPatient.full_name,
      procedure: treatmentFormData.procedure.trim(),
      description: treatmentFormData.description?.trim() || undefined,
      status: treatmentFormData.status || 'Planejado',
      priority: treatmentFormData.priority || 'Média',

      // NOVOS CAMPOS CFO
      diagnosis: treatmentFormData.diagnosis?.trim() || undefined,
      treatment_plan: treatmentFormData.treatment_plan?.trim() || undefined,
      alternatives: treatmentFormData.alternatives?.trim() || undefined,
      risks: treatmentFormData.risks?.trim() || undefined,

      ai_suggested: false,
    };

    const calculatedCost = calculateFinalCost(
      treatmentFormData.cost,
      treatmentFormData.discount_percent
    );

    if (calculatedCost.cost !== undefined) {
      payload.cost = calculatedCost.cost;
      payload.discount_percent = calculatedCost.discountPercent;
      payload.discount_amount = calculatedCost.discountAmount;
      payload.final_cost = calculatedCost.finalCost;
    }

    try {
      setCreating(true);
      let res;

      if (selectedTreatment?.id) {
        res = await axios.put(`${API_URL}/treatments/${selectedTreatment.id}`, payload, {
          headers: getAuthHeaders(),
        });

        const updated = res?.data;
        setTreatments((prev) =>
          prev.map((item) => (item.id === selectedTreatment.id ? updated : item))
        );

        toast?.success?.('Tratamento atualizado com sucesso!');
        closeTreatmentModal();
        resetTreatmentForm();

        if (onDataUpdate) {
          await onDataUpdate();
        }

        return;
      }

      res = await axios.post(`${API_URL}/treatments`, payload, {
        headers: getAuthHeaders(),
      });
      const created = res?.data;
      if (created && created.id) {
        setTreatments((prev) => [created, ...prev]);

        if (logAudit) {
          await logAudit('CRIOU_PLANO_TRATAMENTO', {
            treatment_id: created.id,
            procedure: created.procedure || payload.procedure,
            diagnosis: payload.diagnosis || null,
            treatment_plan: payload.treatment_plan || null,
          });
        }
      } else {
        setTreatments((prev) => [{ ...payload, id: `tmp-${Date.now()}` }, ...prev]);
      }
      toast?.success?.('Tratamento criado com sucesso!');
      setShowNewTreatmentModal(false);

      if (onDataUpdate) {
        await onDataUpdate();
      }

      setTreatmentFormData({
        procedure: '',
        description: '',
        cost: '',
        discount_percent: '',
        discount_amount: '',
        final_cost: '',
        priority: 'Média',
        status: 'Planejado',

        diagnosis: '',
        treatment_plan: '',
        alternatives: '',
        risks: '',
      });
    } catch (error) {
      toast?.error?.(extractErrorMsg(error, 'Erro ao criar tratamento.'));
    } finally {
      setCreating(false);
    }
  };

  const handleInactiveTreatment = async (t) => {
    if (!t?.id) return;

    if (
      t.source === 'aligners' ||
      t.source === 'aligners_import' ||
      t.aligner_treatment_id
    ) {
      toast?.error?.(
        'Este plano pertence ao módulo de Alinhadores. ' +
          'Faça a inativação pela aba Alinhadores.'
      );

      return;
    }

    const reason = window.prompt(
      'Informe o motivo da inativação deste plano de tratamento.\nO registro NÃO será apagado do prontuário.'
    );

    if (reason === null) return;

    if (!reason.trim()) {
      toast?.error?.('Informe um motivo para inativar o tratamento.');
      return;
    }

    const confirmed = window.confirm(
      'Confirmar inativação deste plano de tratamento? Ele continuará visível no histórico.'
    );
    if (!confirmed) return;

    try {
      setDeletingId(t.id);

      const payload = {
        ...t,
        status: 'Plano Inativado',
        is_inactive: true,
        inactive_at: new Date().toISOString(),
        inactive_reason: reason.trim(),
      };

      const res = await axios.put(
        `${API_URL}/treatments/${t.id}`,
        payload,
        { headers: getAuthHeaders() }
      );

      setTreatments((prev) =>
        prev.map((item) => (item.id === t.id ? res.data : item))
      );

      toast?.success?.('Plano de tratamento inativado.');
    } catch (error) {
      toast?.error?.(extractErrorMsg(error, 'Erro ao inativar tratamento.'));
    } finally {
      setDeletingId(null);
    }
  };

  const refreshTreatmentAcceptances = async () => {
    if (!patientId) return;

    try {
      setLoading(true);

      const res = await axios.get(`${API_URL}/treatments`, {
        params: { patient_id: patientId },
        headers: getAuthHeaders(),
      });

      const arr = Array.isArray(res?.data?.items)
        ? res.data.items
        : Array.isArray(res?.data)
        ? res.data
        : [];

      setTreatments(arr);
      toast?.success?.('Aceites dos tratamentos atualizados.');
    } catch (error) {
      toast?.error?.(extractErrorMsg(error, 'Erro ao atualizar aceites.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAcceptanceLink = async (treatment) => {
    if (!treatment?.id) {
      toast?.error?.('Tratamento inválido para gerar link de aceite.');
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/treatments/${treatment.id}/acceptance-link`,
        {},
        { headers: getAuthHeaders() }
      );

      const publicPath = data?.public_path;
      if (!publicPath) {
        throw new Error('O backend não retornou o link público.');
      }

      const fullLink = `${window.location.origin}${publicPath}`;
      await navigator.clipboard.writeText(fullLink);

      if (logAudit) {
        await logAudit('GEROU_LINK_ACEITE_TRATAMENTO', {
          treatment_id: treatment.id,
          procedure: treatment.procedure || null,
          public_path: publicPath,
        });
      }

      toast?.success?.('Link de aceite copiado para a área de transferência.');
    } catch (error) {
      toast?.error?.(extractErrorMsg(error, 'Erro ao gerar link de aceite.'));
    }
  };

  const handleOpenInvoiceModal = async (treatment) => {
    setSelectedTreatmentForInvoice(treatment);
    setShowInvoiceModal(true);
    setInvoiceOption('new');
    setSelectedExistingInvoiceId('');
    try {
      const res = await axios.get(`${API_URL}/billing/invoices`, {
        params: { patient_id: patientId },
        headers: getAuthHeaders(),
      });
      // Filtrar faturas que não estão canceladas
      const arr = Array.isArray(res?.data) ? res.data : [];
      setInvoicesList(arr.filter(i => i.status !== 'cancelado'));
    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
    }
  };

  const handleProcessInvoice = async () => {
    if (invoiceOption === 'link' && !selectedExistingInvoiceId) {
       toast?.error?.('Selecione a fatura para vincular.');
       return;
    }

    setIsInvoicing(true);
    try {
      let finalInvoiceId = selectedExistingInvoiceId;

      if (invoiceOption === 'new') {
         // Create Invoice
         const invoicePayload = {
            patient_id: patientId,
            patient_name: selectedPatient.full_name,
            due_date: new Date().toISOString(),
            status: "pendente",
            items: [
              {
                description: selectedTreatmentForInvoice.procedure,
                quantity: 1,
                unit_price: Number(
                  selectedTreatmentForInvoice.final_cost ?? selectedTreatmentForInvoice.cost ?? 0
                ),
                total_price: Number(
                  selectedTreatmentForInvoice.final_cost ?? selectedTreatmentForInvoice.cost ?? 0
                ),
                treatment_id: selectedTreatmentForInvoice.id,
              }
            ],
            discount_amount: 0,
            discount_percentage: 0,
            notes: "Faturamento gerado a partir de Tratamento."
         };
         const res = await axios.post(`${API_URL}/billing/invoices`, invoicePayload, { headers: getAuthHeaders() });
         finalInvoiceId = res.data.id;
      }

      // Update Treatment
      const updatedTreatmentPayload = { ...selectedTreatmentForInvoice, invoice_id: finalInvoiceId };
      const resTreatment = await axios.put(`${API_URL}/treatments/${selectedTreatmentForInvoice.id}`, updatedTreatmentPayload, { headers: getAuthHeaders() });
      
      setTreatments((prev) => prev.map(t => t.id === selectedTreatmentForInvoice.id ? resTreatment.data : t));

      if (logAudit) {
        await logAudit('VINCULOU_TRATAMENTO_A_FATURA', {
          treatment_id: selectedTreatmentForInvoice.id,
          invoice_id: finalInvoiceId,
          procedure: selectedTreatmentForInvoice.procedure || null,
        });
      }

      toast?.success?.('Tratamento faturado/vinculado com sucesso!');
      setShowInvoiceModal(false);
    } catch (e) {
      toast?.error?.(extractErrorMsg(e, 'Erro ao processar faturamento.'));
    } finally {
      setIsInvoicing(false);
    }
  };

  if (loading) return <div className="p-3 text-gray-600">Carregando…</div>;
  if (err) return <div className="p-3 text-red-600">{err}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Tratamentos ({visibleTreatments.length})
        </h3>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refreshTreatmentAcceptances}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
            disabled={loading}
            title="Atualizar status dos aceites dos tratamentos"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Atualizar aceites
          </button>

          <button
            type="button"
            onClick={() => setShowPrintListModal(true)}
            className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center"
            title="Imprimir resumo dos tratamentos"
          >
            <FileText className="w-4 h-4 mr-2" />
            Imprimir Lista
          </button>

          <button
            type="button"
            onClick={handleOpenPriceTableModal}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            title="Gerenciar tabela de valores da clínica"
          >
            <TableProperties className="w-4 h-4 mr-2" />
            Tabela de Valores
          </button>

          <button
            onClick={() => {
              resetTreatmentForm();
              setShowNewTreatmentModal(true);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Tratamento
          </button>
        </div>
      </div>

      {listFilter === 'stalled' && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold text-blue-900">
                Filtro aplicado: tratamentos parados
              </p>
              <p className="mt-1 text-sm text-blue-700">
                Em andamento ou ativos, sem atualização há 90 dias ou mais.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setListFilter('')}
              className="inline-flex items-center justify-center rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
            >
              <X className="mr-2 h-4 w-4" />
              Mostrar todos
            </button>
          </div>
        </div>
      )}

      {visibleTreatments.length > 0 ? (
        <div className="space-y-4">
          {visibleTreatments.map((t) => {
            const linkedAligner =
              alignerByLinkedTreatmentId.get(t.id) ||
              alignerTreatments.find(
                (aligner) =>
                  aligner.id === t.aligner_treatment_id
              ) ||
              null;

            return (
              <div key={t.id} className="bg-gray-50 p-4 rounded-lg border">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-900">{t.procedure}</h4>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      t.status === 'Concluído'
                        ? 'bg-green-100 text-green-800'
                        : t.status === 'Em Andamento'
                        ? 'bg-yellow-100 text-yellow-800'
                        : t.status === 'Planejado'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {t.status}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleOpenTreatment(t)}
                    className="p-1.5 flex items-center text-xs gap-1 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
                    title="Abrir ou editar tratamento"
                  >
                    <Edit3 className="w-3 h-3" />
                    Abrir / Editar
                  </button>

                  <button
                    type="button"
                    onClick={() => handlePrintTreatment(t)}
                    className="p-1.5 flex items-center text-xs gap-1 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200"
                    title="Imprimir plano de tratamento"
                  >
                    <Printer className="w-3 h-3" />
                    Imprimir
                  </button>

                  <button
                    onClick={() => handleCopyAcceptanceLink(t)}
                    disabled={t.is_inactive}
                    className="p-1.5 flex items-center text-xs gap-1 rounded-md bg-green-50 hover:bg-green-100 text-green-700 border border-green-200"
                    title="Gerar e copiar link de aceite do plano de tratamento"
                  >
                    <LinkIcon className="w-3 h-3" />
                    <Copy className="w-3 h-3" />
                    Aceite
                  </button>

                  {linkedAligner ? (
                    <span
                      className="
                        flex
                        items-center
                        gap-1
                        rounded-full
                        bg-purple-100
                        px-2
                        py-1
                        text-xs
                        font-medium
                        text-purple-800
                      "
                      title="O controle financeiro automático está desativado para tratamentos vinculados ao módulo de alinhadores."
                    >
                      <Activity className="h-3 w-3" />

                      {linkedAligner?.commercial_model ===
                      'manufacturer_direct'
                        ? 'Cobrança pelo fabricante'
                        : linkedAligner?.commercial_model ===
                          'clinic_resale'
                        ? 'Financeiro não integrado'
                        : linkedAligner?.commercial_model ===
                          'hybrid'
                        ? 'Modelo financeiro híbrido'
                        : 'Financeiro não integrado'}
                    </span>
                  ) : t.invoice_id ? (
                    <span
                      className="
                        flex
                        items-center
                        gap-1
                        rounded-full
                        bg-indigo-100
                        px-2
                        py-1
                        text-xs
                        font-medium
                        text-indigo-800
                      "
                      title="Ver faturas na aba principal"
                    >
                      <Receipt className="h-3 w-3" />

                      Faturado
                    </span>
                  ) : (
                    <button
                      onClick={() => handleOpenInvoiceModal(t)}
                      disabled={t.is_inactive}
                      className="
                        flex
                        items-center
                        gap-1
                        rounded-md
                        border
                        border-indigo-200
                        bg-indigo-50
                        p-1.5
                        text-xs
                        text-indigo-600
                        hover:bg-indigo-100
                      "
                      title="Faturar tratamento"
                    >
                      <Receipt className="h-3 w-3" />

                      Faturar
                    </button>
                  )}

                  {!t.is_inactive &&
                    !linkedAligner &&
                    t.source !== 'aligners' &&
                    t.source !== 'aligners_import' && (
                    <button
                      onClick={() => handleInactiveTreatment(t)}
                      className="p-1.5 rounded-md hover:bg-red-50 text-red-600"
                      title="Inativar plano de tratamento"
                      disabled={deletingId === t.id}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {t.description && (
                <p className="text-sm text-gray-600 mb-2">{t.description}</p>
              )}

              {linkedAligner && (
                <div className="mb-3 rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm">
                  <div className="font-semibold text-purple-900">
                    Vinculado ao módulo de Alinhadores
                  </div>

                  <div className="mt-1 text-xs text-purple-700">
                    Plano criado e sincronizado automaticamente.
                  </div>

                  <div className="mt-1 text-purple-800">
                    <strong>Fabricante:</strong>{' '}
                    {linkedAligner.provider || 'Não informado'}
                  </div>

                  <div className="text-purple-800">
                    <strong>Modelo comercial:</strong>{' '}

                    {linkedAligner.commercial_model ===
                    'manufacturer_direct'
                      ? 'Cobrança direta pelo fabricante'
                      : linkedAligner.commercial_model ===
                        'clinic_resale'
                      ? 'Compra e revenda pela clínica'
                      : linkedAligner.commercial_model ===
                        'hybrid'
                      ? 'Modelo híbrido'
                      : linkedAligner.commercial_model ===
                        'other'
                      ? 'Outro modelo'
                      : 'Não controlado pelo OdontoAssist'}
                  </div>

                  {linkedAligner.commercial_notes && (
                    <div className="mt-1 whitespace-pre-wrap text-purple-700">
                      {linkedAligner.commercial_notes}
                    </div>
                  )}

                  <div className="mt-2 text-xs text-purple-700">
                    Nenhuma fatura é gerada automaticamente para
                    este plano.
                  </div>
                </div>
              )}

              {t.diagnosis && (
                <p className="text-sm text-gray-700 mb-1">
                  <strong>Diagnóstico:</strong> {t.diagnosis}
                </p>
              )}

              {t.treatment_plan && (
                <p className="text-sm text-gray-700 mb-1">
                  <strong>Plano:</strong> {t.treatment_plan}
                </p>
              )}

              {t.alternatives && (
                <p className="text-sm text-gray-700 mb-1">
                  <strong>Alternativas:</strong> {t.alternatives}
                </p>
              )}

              {t.risks && (
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Riscos:</strong> {t.risks}
                </p>
              )}

              {t.is_inactive && (
                <div className="mb-2 p-2 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700">
                  <div className="font-semibold">Plano de tratamento inativado</div>

                  {t.inactive_at && (
                    <div>
                      Inativado em:{' '}
                      {new Date(t.inactive_at).toLocaleString('pt-BR')}
                    </div>
                  )}

                  {t.inactive_reason && (
                    <div>Motivo: {t.inactive_reason}</div>
                  )}
                </div>
              )}

              <div className="mt-2 mb-2 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  <span>
                    Aceite do paciente:{' '}
                    {t.patient_acceptance_status === 'aceito'
                      ? 'Aceito por link'
                      : t.patient_acceptance_status === 'recusado'
                      ? 'Recusado'
                      : 'Pendente'}
                  </span>
                </div>

                {t.patient_acceptance_at && (
                  <div className="ml-4 text-[11px] text-gray-500">
                    Confirmado em: {new Date(t.patient_acceptance_at).toLocaleString('pt-BR')}
                    {t.patient_acceptance_ip ? ` • IP: ${t.patient_acceptance_ip}` : ''}
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center text-sm text-gray-500">
                <div className="flex flex-wrap gap-4">
                  <span>
                    Prioridade: <span className="font-medium">{t.priority || '—'}</span>
                  </span>
                  {t.cost != null && t.cost !== '' && (
                    <span>
                      Valor:{' '}
                      <span className="font-medium text-green-600">
                        {formatCurrency
                          ? formatCurrency(t.final_cost ?? t.cost)
                          : (Number(t.final_cost ?? t.cost) || 0).toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            })}
                      </span>

                      {t.discount_percent ? (
                        <span className="ml-1 text-xs text-gray-500">
                          ({t.discount_percent}% desc.)
                        </span>
                      ) : null}
                    </span>
                  )}
                  {t.date_performed && (
                    <span>
                      Data:{' '}
                      <span className="font-medium">
                        {formatDate ? formatDate(t.date_performed) : new Date(t.date_performed).toLocaleString('pt-BR')}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <Activity className="w-6 h-6 mx-auto text-gray-400 mb-2" />
          <p className="text-gray-600">
            {listFilter === 'stalled'
              ? 'Este paciente não possui tratamento parado pelos critérios atuais.'
              : 'Nenhum tratamento cadastrado para este paciente.'}
          </p>
          {listFilter === 'stalled' && (
            <button
              type="button"
              onClick={() => setListFilter('')}
              className="mt-3 text-sm font-semibold text-purple-600 hover:text-purple-700"
            >
              Mostrar todos os tratamentos
            </button>
          )}
        </div>
      )}

      {showPriceTableModal && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
          onClick={() => setShowPriceTableModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Tabela de Valores da Clínica
                </h3>

                <button
                  type="button"
                  onClick={() => setShowPriceTableModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="text-sm text-gray-500 mt-1">
                Cadastre os procedimentos e valores usados pela clínica. Esses valores são apenas padrão interno e podem ser alterados em cada plano.
              </p>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <form onSubmit={handleSavePriceItem} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tratamento / procedimento *
                  </label>
                  <input
                    type="text"
                    required
                    value={priceItemForm.procedure}
                    onChange={(e) =>
                      setPriceItemForm((p) => ({ ...p, procedure: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Ex.: Restauração"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição padrão
                  </label>
                  <textarea
                    value={priceItemForm.description}
                    onChange={(e) =>
                      setPriceItemForm((p) => ({ ...p, description: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Descrição opcional para preencher no plano"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor padrão
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={priceItemForm.default_cost}
                      onChange={(e) =>
                        setPriceItemForm((p) => ({ ...p, default_cost: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="Ex.: 250,00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prioridade padrão
                    </label>
                    <select
                      value={priceItemForm.default_priority}
                      onChange={(e) =>
                        setPriceItemForm((p) => ({ ...p, default_priority: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="Alta">Alta</option>
                      <option value="Média">Média</option>
                      <option value="Baixa">Baixa</option>
                    </select>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={priceItemForm.is_active}
                    onChange={(e) =>
                      setPriceItemForm((p) => ({ ...p, is_active: e.target.checked }))
                    }
                  />
                  Item ativo
                </label>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={savingPriceItem}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60"
                  >
                    {savingPriceItem
                      ? 'Salvando...'
                      : editingPriceItem?.id
                      ? 'Salvar alterações'
                      : 'Adicionar item'}
                  </button>

                  {editingPriceItem?.id && (
                    <button
                      type="button"
                      onClick={resetPriceItemForm}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Cancelar edição
                    </button>
                  )}
                </div>
              </form>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">
                  Procedimentos cadastrados
                </h4>

                {priceTable.length > 0 ? (
                  <div className="space-y-2">
                    {priceTable.map((item) => (
                      <div
                        key={item.id}
                        className={`border rounded-lg p-3 ${
                          item.is_active === false ? 'bg-gray-50 opacity-70' : 'bg-white'
                        }`}
                      >
                        <div className="flex justify-between gap-3">
                          <div>
                            <div className="font-medium text-gray-900">
                              {item.procedure}
                            </div>

                            {item.description && (
                              <div className="text-xs text-gray-500 mt-1">
                                {item.description}
                              </div>
                            )}

                            <div className="text-sm text-green-700 mt-1">
                              {item.default_cost != null
                                ? formatCurrency
                                  ? formatCurrency(item.default_cost)
                                  : Number(item.default_cost).toLocaleString('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                    })
                                : 'Sem valor definido'}
                              {' • '}
                              Prioridade: {item.default_priority || 'Média'}
                              {item.is_active === false ? ' • Inativo' : ''}
                            </div>
                          </div>

                          <div className="flex items-start gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditPriceItem(item)}
                              className="px-2 py-1 text-xs border rounded-md hover:bg-gray-50"
                            >
                              Editar
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeletePriceItem(item)}
                              className="px-2 py-1 text-xs border border-red-200 text-red-600 rounded-md hover:bg-red-50"
                            >
                              Excluir
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-500">
                    Nenhum procedimento cadastrado ainda.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showPrintListModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowPrintListModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Imprimir lista de tratamentos
                </h3>
                <button
                  type="button"
                  onClick={() => setShowPrintListModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Selecione um período ou deixe em branco para imprimir todos.
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Data inicial
                  </span>
                  <input
                    type="date"
                    value={printPeriodStart}
                    onChange={(e) => setPrintPeriodStart(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </label>

                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Data final
                  </span>
                  <input
                    type="date"
                    value={printPeriodEnd}
                    onChange={(e) => setPrintPeriodEnd(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Status do plano
                  </span>
                  <select
                    value={printActiveFilter}
                    onChange={(e) => setPrintActiveFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="todos">Todos</option>
                    <option value="ativos">Somente ativos</option>
                    <option value="inativos">Somente inativos</option>
                  </select>
                </label>

                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    Aceite do paciente
                  </span>
                  <select
                    value={printAcceptanceFilter}
                    onChange={(e) => setPrintAcceptanceFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="todos">Todos</option>
                    <option value="aceitos">Somente com aceite</option>
                    <option value="pendentes">Somente sem aceite</option>
                  </select>
                </label>
              </div>

              <button
                type="button"
                onClick={() => {
                  setPrintPeriodStart('');
                  setPrintPeriodEnd('');
                  setPrintActiveFilter('todos');
                  setPrintAcceptanceFilter('todos');
                }}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Limpar período e imprimir todos
              </button>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowPrintListModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handlePrintTreatmentsList}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 flex items-center"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimir
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewTreatmentModal && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
          onClick={() => closeTreatmentModal()}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedTreatment?.id ? 'Abrir / Editar Tratamento' : 'Novo Tratamento'}
                </h3>
                <button
                  onClick={() => closeTreatmentModal()}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Paciente: <span className="font-medium">{selectedPatient?.full_name || '—'}</span>
              </p>
            </div>

            <form onSubmit={handleNewTreatmentSubmit} className="p-6 space-y-4">
              {!selectedTreatment?.id && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                  Tratamentos com alinhadores devem ser criados pela aba
                  <strong> Alinhadores</strong>. O plano correspondente
                  aparecerá automaticamente nesta aba.
                </div>
              )}

              {priceTable.length > 0 && (
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
                  <label className="block text-sm font-medium text-purple-900 mb-1">
                    Usar tratamento pré-definido
                  </label>
                  <select
                    defaultValue=""
                    onChange={(e) => handleSelectPriceTableItem(e.target.value)}
                    className="w-full border border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="">Selecione para preencher procedimento e valor</option>
                    {priceTable
                      .filter((item) => item.is_active !== false)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.procedure}
                          {item.default_cost != null
                            ? ` — ${
                                formatCurrency
                                  ? formatCurrency(item.default_cost)
                                  : Number(item.default_cost).toLocaleString('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                    })
                              }`
                            : ''}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-purple-700 mt-1">
                    Ao selecionar, o procedimento, a descrição, o valor bruto e a prioridade serão preenchidos automaticamente.
                  </p>
                </div>
              )}        
        
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Procedimento *
                </label>
                <input
                  type="text"
                  required
                  value={treatmentFormData.procedure}
                  onChange={(e) =>
                    setTreatmentFormData((p) => ({ ...p, procedure: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  placeholder="Ex.: Restauração, Limpeza..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={treatmentFormData.description}
                  onChange={(e) =>
                    setTreatmentFormData((p) => ({ ...p, description: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  rows="3"
                  placeholder="Detalhes do tratamento (opcional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Diagnóstico / Achado clínico
                </label>
                <textarea
                  value={treatmentFormData.diagnosis}
                  onChange={(e) =>
                    setTreatmentFormData((p) => ({ ...p, diagnosis: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  rows="2"
                  placeholder="Ex.: Cárie profunda em molar 36"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plano de tratamento proposto
                </label>
                <textarea
                  value={treatmentFormData.treatment_plan}
                  onChange={(e) =>
                    setTreatmentFormData((p) => ({ ...p, treatment_plan: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  rows="3"
                  placeholder="Descreva o plano completo de tratamento"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alternativas terapêuticas
                </label>
                <textarea
                  value={treatmentFormData.alternatives}
                  onChange={(e) =>
                    setTreatmentFormData((p) => ({ ...p, alternatives: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  rows="2"
                  placeholder="Ex.: tratamento conservador, extração..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Riscos e limitações
                </label>
                <textarea
                  value={treatmentFormData.risks}
                  onChange={(e) =>
                    setTreatmentFormData((p) => ({ ...p, risks: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  rows="2"
                  placeholder="Ex.: possibilidade de dor, retratamento..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custo bruto</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={treatmentFormData.cost}
                    onChange={(e) =>
                      setTreatmentFormData((p) => ({ ...p, cost: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Ex.: 150,00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desconto (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={treatmentFormData.discount_percent}
                    onChange={(e) =>
                      setTreatmentFormData((p) => ({ ...p, discount_percent: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="Ex.: 10"
                  />
                </div>

                {safeNumber(treatmentFormData.cost) !== undefined && (
                  <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                    {(() => {
                      const calculated = calculateFinalCost(
                        treatmentFormData.cost,
                        treatmentFormData.discount_percent
                      );

                      return (
                        <div className="space-y-1">
                          <div>
                            Valor bruto:{' '}
                            <strong>
                              {formatCurrency
                                ? formatCurrency(calculated.cost || 0)
                                : (calculated.cost || 0).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  })}
                            </strong>
                          </div>

                          <div>
                            Desconto:{' '}
                            <strong>
                              {calculated.discountPercent || 0}% —{' '}
                              {formatCurrency
                                ? formatCurrency(calculated.discountAmount || 0)
                                : (calculated.discountAmount || 0).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  })}
                            </strong>
                          </div>

                          <div>
                            Valor final:{' '}
                            <strong>
                              {formatCurrency
                                ? formatCurrency(calculated.finalCost || 0)
                                : (calculated.finalCost || 0).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  })}
                            </strong>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                  <select
                    value={treatmentFormData.priority}
                    onChange={(e) =>
                      setTreatmentFormData((p) => ({ ...p, priority: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Alta">Alta</option>
                    <option value="Média">Média</option>
                    <option value="Baixa">Baixa</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={treatmentFormData.status}
                  onChange={(e) =>
                    setTreatmentFormData((p) => ({ ...p, status: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="Planejado">Planejado</option>
                  <option value="Em Andamento">Em Andamento</option>
                  <option value="Concluído">Concluído</option>
                  <option value="Cancelado">Cancelado</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => closeTreatmentModal()}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={creating}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center disabled:opacity-60"
                  disabled={creating}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {creating
                    ? selectedTreatment?.id ? 'Salvando...' : 'Criando...'
                    : selectedTreatment?.id ? 'Salvar Alterações' : 'Criar Tratamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showInvoiceModal && selectedTreatmentForInvoice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowInvoiceModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Faturar Tratamento</h3>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                <strong>{selectedTreatmentForInvoice.procedure}</strong>

                <br />

                Valor bruto:{' '}
                <strong>
                  {formatCurrency
                    ? formatCurrency(selectedTreatmentForInvoice.cost || 0)
                    : (Number(selectedTreatmentForInvoice.cost) || 0).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                </strong>

                {Number(selectedTreatmentForInvoice.discount_percent || 0) > 0 && (
                  <>
                    <br />
                    Desconto:{' '}
                    <strong>
                      {selectedTreatmentForInvoice.discount_percent}% (
                      {formatCurrency
                        ? formatCurrency(selectedTreatmentForInvoice.discount_amount || 0)
                        : (Number(selectedTreatmentForInvoice.discount_amount) || 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                      )
                    </strong>

                    <br />
                    Valor final:{' '}
                    <strong className="text-green-700">
                      {formatCurrency
                        ? formatCurrency(
                            selectedTreatmentForInvoice.final_cost ??
                              selectedTreatmentForInvoice.cost ??
                              0
                          )
                        : (Number(
                            selectedTreatmentForInvoice.final_cost ??
                              selectedTreatmentForInvoice.cost
                          ) || 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                    </strong>
                  </>
                )}
              </p>
            </div>

            <div className="p-6 space-y-4">
               <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ação de Faturamento</label>
                  <div className="space-y-2">
                     <label className="flex items-center space-x-2">
                        <input type="radio" value="new" checked={invoiceOption === 'new'} onChange={() => setInvoiceOption('new')} className="text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-sm text-gray-700">Gerar Nova Fatura</span>
                     </label>
                     <label className="flex items-center space-x-2">
                        <input type="radio" value="link" checked={invoiceOption === 'link'} onChange={() => setInvoiceOption('link')} className="text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-sm text-gray-700">Vincular a Fatura Existente</span>
                     </label>
                  </div>
               </div>

               {invoiceOption === 'link' && (
                  <div className="mt-4">
                     <label className="block text-sm font-medium text-gray-700 mb-1">Selecione a Fatura</label>
                     <select
                        value={selectedExistingInvoiceId}
                        onChange={(e) => setSelectedExistingInvoiceId(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                     >
                        <option value="">-- Selecione --</option>
                        {invoicesList.map(inv => (
                           <option key={inv.id} value={inv.id}>
                              Fatura #{inv.invoice_number} - {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(inv.total_amount)} ({new Date(inv.issue_date).toLocaleDateString('pt-BR')})
                           </option>
                        ))}
                     </select>
                     {invoicesList.length === 0 && (
                        <p className="text-xs text-red-500 mt-1">Nenhuma fatura pendente encontrada para este paciente.</p>
                     )}
                  </div>
               )}
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
               <button
                  type="button"
                  onClick={() => setShowInvoiceModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  disabled={isInvoicing}
               >
                  Cancelar
               </button>
               <button
                  type="button"
                  onClick={handleProcessInvoice}
                  disabled={isInvoicing || (invoiceOption === 'link' && !selectedExistingInvoiceId)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center disabled:opacity-60"
               >
                  <Receipt className="w-4 h-4 mr-2" />
                  {isInvoicing ? 'Processando...' : 'Confirmar'}
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentsTab;
